/**
 * YouTube OAuth Authentication Service
 * 
 * Handles Google OAuth 2.0 flow for YouTube Data API access
 * 
 * SETUP REQUIRED:
 * 1. Go to Google Cloud Console (https://console.cloud.google.com)
 * 2. Create a new project or select existing
 * 3. Enable "YouTube Data API v3"
 * 4. Go to Credentials → Create Credentials → OAuth 2.0 Client ID
 * 5. Application type: Web application
 * 6. Add authorized JavaScript origins: http://localhost:3000 (dev), your production domain
 * 7. Add authorized redirect URIs: http://localhost:3000/services/youtube/callback
 * 8. Copy the Client ID to your .env file as REACT_APP_GOOGLE_CLIENT_ID
 */

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
const YOUTUBE_SCOPES = [
  'https://www.googleapis.com/auth/youtube.readonly',
  'https://www.googleapis.com/auth/youtube',
].join(' ');

// Store token in localStorage
const TOKEN_KEY = 'jookbox_youtube_token';
const TOKEN_EXPIRY_KEY = 'jookbox_youtube_token_expiry';

class YouTubeAuthService {
  constructor() {
    this.accessToken = localStorage.getItem(TOKEN_KEY);
    this.tokenExpiry = localStorage.getItem(TOKEN_EXPIRY_KEY);
    this.isInitialized = false;
    this.googleAuth = null;
  }

  /**
   * Initialize Google Identity Services
   */
  async initialize() {
    if (this.isInitialized) return;

    return new Promise((resolve, reject) => {
      // Load Google Identity Services library
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = () => {
        console.log('✅ Google Identity Services loaded');
        this.isInitialized = true;
        resolve();
      };
      script.onerror = () => {
        reject(new Error('Failed to load Google Identity Services'));
      };
      document.body.appendChild(script);
    });
  }

  /**
   * Check if user is authenticated with YouTube
   */
  isAuthenticated() {
    if (!this.accessToken) return false;
    
    // Check if token is expired
    if (this.tokenExpiry) {
      const expiry = new Date(this.tokenExpiry);
      if (expiry < new Date()) {
        this.clearToken();
        return false;
      }
    }
    
    return true;
  }

  /**
   * Get the current access token
   */
  getAccessToken() {
    if (this.isAuthenticated()) {
      return this.accessToken;
    }
    return null;
  }

  /**
   * Initiate Google OAuth flow
   */
  async signIn() {
    await this.initialize();

    if (!GOOGLE_CLIENT_ID) {
      throw new Error('Google Client ID not configured. Please set REACT_APP_GOOGLE_CLIENT_ID in your .env file.');
    }

    return new Promise((resolve, reject) => {
      // Use the newer Google Identity Services token model
      const tokenClient = window.google.accounts.oauth2.initTokenClient({
        client_id: GOOGLE_CLIENT_ID,
        scope: YOUTUBE_SCOPES,
        callback: (response) => {
          if (response.error) {
            console.error('❌ YouTube OAuth error:', response.error);
            reject(new Error(response.error_description || response.error));
            return;
          }

          console.log('✅ YouTube OAuth successful');
          
          // Store the token
          this.accessToken = response.access_token;
          this.tokenExpiry = new Date(Date.now() + response.expires_in * 1000).toISOString();
          
          localStorage.setItem(TOKEN_KEY, this.accessToken);
          localStorage.setItem(TOKEN_EXPIRY_KEY, this.tokenExpiry);
          
          resolve({
            accessToken: this.accessToken,
            expiresIn: response.expires_in
          });
        },
      });

      // Request the token (will show Google sign-in popup)
      tokenClient.requestAccessToken();
    });
  }

  /**
   * Sign out from YouTube
   */
  signOut() {
    this.clearToken();
    
    // Revoke the token if possible
    if (this.accessToken && window.google?.accounts?.oauth2) {
      window.google.accounts.oauth2.revoke(this.accessToken, () => {
        console.log('✅ YouTube token revoked');
      });
    }
  }

  /**
   * Clear stored token
   */
  clearToken() {
    this.accessToken = null;
    this.tokenExpiry = null;
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(TOKEN_EXPIRY_KEY);
  }

  /**
   * Get user info from YouTube
   */
  async getUserInfo() {
    if (!this.isAuthenticated()) {
      throw new Error('Not authenticated with YouTube');
    }

    const response = await fetch(
      'https://www.googleapis.com/youtube/v3/channels?part=snippet&mine=true',
      {
        headers: {
          'Authorization': `Bearer ${this.accessToken}`
        }
      }
    );

    if (!response.ok) {
      if (response.status === 401) {
        this.clearToken();
        throw new Error('YouTube session expired. Please sign in again.');
      }
      throw new Error('Failed to fetch YouTube user info');
    }

    const data = await response.json();
    
    if (data.items && data.items.length > 0) {
      const channel = data.items[0];
      return {
        id: channel.id,
        title: channel.snippet.title,
        thumbnail: channel.snippet.thumbnails?.default?.url,
        description: channel.snippet.description
      };
    }

    return null;
  }
}

// Export singleton instance
export const youtubeAuth = new YouTubeAuthService();
export default youtubeAuth;




